import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, Link2, Unlink, Trash2, Loader2, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: '', payer_name: '', note: '', payment_date: '' });
  const [saving, setSaving] = useState(false);
  const [matchTarget, setMatchTarget] = useState(null);
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/payments`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/attendees`, { headers: getAuthHeaders() }),
      ]);
      setPayments(pRes.data);
      setAttendees(aRes.data);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addPayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/payments`, {
        amount: parseFloat(form.amount),
        payer_name: form.payer_name,
        note: form.note,
        payment_date: form.payment_date,
      }, { headers: getAuthHeaders() });
      toast.success('Payment logged');
      setForm({ amount: '', payer_name: '', note: '', payment_date: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add payment');
    } finally {
      setSaving(false);
    }
  };

  const matchPayment = async (attendeeId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/payments/${matchTarget.id}/match`, { attendee_id: attendeeId }, { headers: getAuthHeaders() });
      toast.success(`Matched — new total $${res.data.new_amount_paid.toFixed(0)} (${res.data.new_status})`);
      setMatchTarget(null);
      setSearch('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match');
    }
  };

  const unmatchPayment = async (p) => {
    if (!window.confirm(`Unmatch this $${p.amount} payment from ${p.matched_attendee_name}? Their balance will be reverted.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/payments/${p.id}/unmatch`, {}, { headers: getAuthHeaders() });
      toast.success('Unmatched and reverted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unmatch');
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Delete this $${p.amount} payment entry from ${p.payer_name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/retreat/payments/${p.id}`, { headers: getAuthHeaders() });
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const unmatched = payments.filter(p => p.status === 'unmatched');
  const matched = payments.filter(p => p.status === 'matched');
  const filteredAttendees = attendees.filter(a =>
    `${a.first_name} ${a.last_name} ${a.email}`.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 8);

  if (loading) {
    return <div className="p-6 flex items-center justify-center h-[50vh]"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="payments-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Payment Reconciliation</h1>
        <p className="text-gray-600 mt-1">Log incoming Venmo/check payments, then match them to attendees — balances update automatically.</p>
      </div>

      {/* Add payment */}
      <Card className="border-0 shadow-sm mb-6">
        <CardHeader><CardTitle className="text-base">Log an incoming payment</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={addPayment} className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid="add-payment-form">
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Payer name (from Venmo note)</Label>
              <Input required value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} data-testid="payment-payer-input" />
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="payment-date-input" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="payment-note-input" />
            </div>
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600" data-testid="add-payment-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Add</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Unmatched */}
      <Card className="border-0 shadow-sm mb-6 border-l-4 border-l-amber-400">
        <CardHeader><CardTitle className="text-base">Unmatched payments ({unmatched.length})</CardTitle></CardHeader>
        <CardContent>
          {unmatched.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400" data-testid="unmatched-empty">No unmatched payments — all reconciled</p>
          ) : (
            <div className="space-y-2" data-testid="unmatched-list">
              {unmatched.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                  <div>
                    <p className="font-semibold text-gray-900">${p.amount.toFixed(2)} — {p.payer_name}</p>
                    <p className="text-xs text-gray-500">{p.payment_date || 'no date'}{p.note ? ` · ${p.note}` : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setMatchTarget(p)} data-testid={`match-btn-${p.id}`}>
                      <Link2 className="w-3.5 h-3.5 mr-1.5" /> Match
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePayment(p)} className="text-gray-400 hover:text-red-600" data-testid={`delete-payment-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Matched history */}
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">Matched history ({matched.length})</CardTitle></CardHeader>
        <CardContent>
          {matched.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">Nothing matched yet</p>
          ) : (
            <div className="space-y-2" data-testid="matched-list">
              {matched.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div>
                    <p className="text-sm text-gray-900">
                      <span className="font-semibold">${p.amount.toFixed(2)}</span> from {p.payer_name}
                      {' → '}<Badge variant="outline" className="text-xs">{p.matched_attendee_name}</Badge>
                    </p>
                    <p className="text-xs text-gray-400">{p.matched_at ? new Date(p.matched_at).toLocaleString() : ''}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => unmatchPayment(p)} className="text-gray-400 hover:text-amber-700" data-testid={`unmatch-btn-${p.id}`}>
                    <Unlink className="w-3.5 h-3.5 mr-1.5" /> Unmatch
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Match dialog */}
      <Dialog open={!!matchTarget} onOpenChange={(open) => { if (!open) { setMatchTarget(null); setSearch(''); } }}>
        <DialogContent data-testid="match-dialog">
          <DialogHeader>
            <DialogTitle>Match ${matchTarget?.amount?.toFixed(2)} from "{matchTarget?.payer_name}"</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input autoFocus placeholder="Search attendee by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="match-search-input" />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
            {filteredAttendees.map((a) => (
              <button
                key={a.id}
                onClick={() => matchPayment(a.id)}
                className="w-full text-left p-2.5 rounded-lg hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-colors"
                data-testid={`match-attendee-${a.id}`}
              >
                <p className="text-sm font-medium text-gray-900">{a.first_name} {a.last_name}</p>
                <p className="text-xs text-gray-500">{a.email} · {a.ticket_status} · paid ${(a.amount_paid || 0).toFixed(0)}</p>
              </button>
            ))}
            {search && filteredAttendees.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No matches</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMatchTarget(null); setSearch(''); }}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
